import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

/**
 * Localized display name for a reference-list edition, keyed by the CMS
 * catalogue's stable edition id (`list_catalogs[fw][].id`).
 *
 * The catalogue `name` is authored in English once and is ALSO what the
 * pipeline's edition selection and the LLM prompts read, so it must not be
 * rewritten per locale at the source. This is the display-only layer: known
 * ids resolve through the translation tables, unknown ids (a card citing an
 * edition the catalogue does not carry, a newly added catalogue row without
 * a key yet) fall back to the server name verbatim — never lossy.
 */
const EDITION_NAME_KEYS: Record<string, TranslationKeys> = {
  "svhc-2026-02": "MC_DECL_V2_EDITION_SVHC_2026_02",
  "svhc-2025-11": "MC_DECL_V2_EDITION_SVHC_2025_11",
  "svhc-2025-06": "MC_DECL_V2_EDITION_SVHC_2025_06",
  "svhc-2025-01": "MC_DECL_V2_EDITION_SVHC_2025_01",
  "svhc-2024-06": "MC_DECL_V2_EDITION_SVHC_2024_06",
  "svhc-2023-06": "MC_DECL_V2_EDITION_SVHC_2023_06",
  "annex14-2023-11": "MC_DECL_V2_EDITION_ANNEX14_2023_11",
  "annex17-2026-04": "MC_DECL_V2_EDITION_ANNEX17_2026_04",
  "annex17-2025-06": "MC_DECL_V2_EDITION_ANNEX17_2025_06",
  "rohs-2015-863": "MC_DECL_V2_EDITION_ROHS_2015_863",
  "rohs-2011-65": "MC_DECL_V2_EDITION_ROHS_2011_65",
  "pop-annex1-2025": "MC_DECL_V2_EDITION_POP_ANNEX1_2025",
  "pop-annex1-2023": "MC_DECL_V2_EDITION_POP_ANNEX1_2023",
  "pfas-eu-2026": "MC_DECL_V2_EDITION_PFAS_EU_2026",
  "pfas-oecd-2021": "MC_DECL_V2_EDITION_PFAS_OECD_2021",
  "tsca-6h-2024-11": "MC_DECL_V2_EDITION_TSCA_6H_2024_11",
  "tsca-6h-2021-01": "MC_DECL_V2_EDITION_TSCA_6H_2021_01",
  "cp65-current": "MC_DECL_V2_EDITION_CP65_CURRENT",
  "china-gb26572-2025": "MC_DECL_V2_EDITION_CHINA_GB26572_2025",
  "china-gbt26572-2011": "MC_DECL_V2_EDITION_CHINA_GBT26572_2011",
  "hf-iec-61249-2-21": "MC_DECL_V2_EDITION_HF_IEC_61249_2_21",
  "hf-iec-62474-js709": "MC_DECL_V2_EDITION_HF_IEC_62474_JS709",
}

export function editionDisplayName(
  id: string,
  serverName: string,
  translate: (key: TranslationKeys) => string,
): string {
  const key = EDITION_NAME_KEYS[id]
  return key ? translate(key) : serverName
}
